import { Typography } from '@mui/material';
import { concat } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from 'src/context/ApplicationContext';
import { ALS_ERLEDIGT_MARKIEREN_LABEL, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue } from 'src/lib/validation/ValidationTypes';
import { checkReadOnlyInUeberarbeitung } from 'src/utils/helper';
import PruefAssistenz from '../../../../../../antrag-components/PruefAssistenz';
import { checkIfPruefAssitenzNeedsToValidate } from '../../../../../../application-helper-function/AppHelperfunctions';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
const P = AntragSchema.attributes;

const VorhabensbeschreibungAntragStartupProjekte: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);
	return (
		<>
 			<ApplicationHeading title='Projektbeschreibung' description={PLATZHALTER_TEXT} /> 

			<PruefAssistenz
				statusPath={P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Vorhabensbeschreibung_Pruefstatus.path}
				hinweisPath={P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Vorhabensbeschreibung_Hinweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Vorhabensbeschreibung_Ueberarbeitet_Am.path}
				erledigtPath={P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Vorhabensbeschreibung_Erledigt.path}
				formStruc={formStrucPruefAssistenz()}
			/>

			<CreateForm
				formStruc={formStrucFeld1()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>

			<Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
				Beschreibung der konkreten Angebote, die zur Qualifizierung, zur Steigerung der Wettbewerbsfähigkeit und/oder zur Vernetzung
				von Start-up- und Scale-up-Unternehmen mit Geschäftspartnern und Investoren beitragen.
			</Typography>

			<Typography variant='body2' sx={{ mb: 2 }}>
				z.B. Vernetzungsveranstaltung, Experten-Workshop, Pitch-Vorbereitung, 1:1-Beratung. Bitte das Format <u>verständlich und
				eindeutig</u> darstellen, damit die Beihilferelevanz prüfbar ist: u.a. Zugang offen bzw. begrenzt auf eine ausgewählte Gruppe
				(Auswahlvorgang und -kriterien nennen); Inhalte allgemeingültig / Inhalte unternehmensspezifisch mit konkreten,
				unternehmensbezogenen Handlungsempfehlungen; Vergütung von Experten oder ehrenamtliche Tätigkeit, …).
			</Typography>
			<Typography variant='body2' sx={{ mb: 2 }}>
				Beihilferelevant sind Formate, die begünstigten Start-up-/Scale-up-Unternehmen einen unmittelbaren wirtschaftlichen Vorteil
				gegenüber anderen Unternehmen schaffen. Das ist insbesondere der Fall, wenn es sich bei einem Angebot der Antragstellenden um
				marktübliche Beratung, Coaching oder Weiterbildung handelt, also Inhalte vermittelt werden, die auf unternehmensspezifische
				Fragestellungen zugeschnitten sind und konkrete Handlungsempfehlungen geben, die die wirtschaftliche Position eines
				Unternehmens gegenüber anderen Unternehmen stärken.
			</Typography>
			<Typography variant='body2' sx={{ mb: 2 }}>
				Bei frei zugänglichen, nicht-selektiven Formaten zur offenen Vernetzung oder Wissensvermittlung (allgemeine Informationen,
				keine konkreten, unternehmensspezifischen Inhalte, keine individuellen Handlungsempfehlungen oder unternehmensbezogene
				Beratung) trifft der Beihilfenbegriff i.d.R. nicht zu.
			</Typography>

			<CreateForm
				formStruc={formStrucFelder2bis6()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
		</>
	);
};

export default VorhabensbeschreibungAntragStartupProjekte;

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Vorhabensbeschreibung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};
const formStrucFeld1 = (): FormStrucItem[] => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Star_0134242789_Initiativen_Referenzprojekte,
					maxCharLength: 3000,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucFelder2bis6 = (): FormStrucItem[] => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Star_0237287438_den_Angeboten_Besonderheiten,
					label: '',
					maxCharLength: 8000,
				},
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Star_0095450958_ie_Angebote_Gerichtet_Werden,
					label: 'Konkrete Beschreibung der Zielgruppe/n, an welche die Angebote gerichtet werden. Gemeint sind Start-up- und Scale-up-Unternehmen gemäß Förderrichtlinie Teil I Nr. 3.1. (andere Zielgruppen wie Investoren oder Unternehmen nur angeben, wenn essentiell (z.B. bei Vernetzung))',
					maxCharLength: 2500,
				},
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Star_0077849732_Zielgruppen_Aufgeschluesselt,
					label: 'Anzahl der erwarteten Nutzer der Angebote (insbesondere Start-up- bzw. Scale-up-Unternehmen). Gemeint sind Start-up- und Scale-up-Unternehmen gemäß Förderrichtlinie Teil I Nr. 3.1, wenn möglich aufgeschlüsselt; ggf. weitere Teilnehmer nach Art und Anzahl angegeben.',
					maxCharLength: 2500,
				},
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Star_0256378046_e_Die_Angebote_Unterstuetzen,
					label: 'Angaben zum relevanten Netzwerk und den Kooperationspartnern, welche die beschriebenen Angebote aktiv unterstützen, Namen und Aufgaben benennen.',
					maxCharLength: 2500,
				},
				{
					...P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Projektbeschreibung_Star_0252354308_Reichweiteregionalhessenweit,
					label: 'Beschreibung des Durchführungsorts (z.B. Ort, Veranstaltungsraum / virtuell / hybrid) und der angestrebten räumlichen Reichweite (regional / hessenweit).',
					maxCharLength: 2500,
				}
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

export const getFormStrucVorhabensbeschreibungAntragStartupProjekte = (application: any) => {
	if (
		checkIfPruefAssitenzNeedsToValidate(
			application,
			P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Vorhabensbeschreibung_Pruefstatus.path,
			P.Projektbeschreibung_Start_Up_Projekte.schema.attributes.Pruefung_Vorhabensbeschreibung_Erledigt.path,
		)
	)
		return concat(formStrucPruefAssistenz(), formStrucFeld1(), formStrucFelder2bis6());
	return concat(formStrucFeld1(), formStrucFelder2bis6());
};
